import type { ChatOut, GroupChatOut } from '../types'
import { isEventChat, isGroupChat } from '../types/ChatMessage'
import { CHAT_TYPES, isGroupLikeChatType } from '../constants'

interface GroupAdminContext {
  matrixUserId?: string | null
  matrixRoomAdmin?: boolean
}

export class ChatPermissions {
  private static matchesAnyId(
    value: string | number | null | undefined,
    ...candidates: Array<string | number | null | undefined>
  ): boolean {
    if (value === null || value === undefined) {
      return false
    }

    const normalized = String(value).trim()
    if (!normalized) {
      return false
    }

    return candidates.some(
      candidate =>
        candidate !== null
        && candidate !== undefined
        && String(candidate).trim() === normalized,
    )
  }

  private static isGroupAdmin(
    chat: GroupChatOut,
    currentUserId: string | number | null | undefined,
    context: GroupAdminContext = {},
  ): boolean {
    const { matrixUserId = null, matrixRoomAdmin = false } = context
    const userIds = [currentUserId, matrixUserId]

    if (chat.owner && userIds.some(id => this.matchesAnyId(chat.owner, id))) {
      return true
    }

    if (chat.created_by) {
      const creatorIds = [chat.created_by.id, chat.created_by.uuid]
      if (creatorIds.some(creatorId => userIds.some(id => this.matchesAnyId(creatorId, id)))) {
        return true
      }
    }

    const adminMember = chat.members.find(
      member =>
        member.is_admin
        && userIds.some(
          id =>
            this.matchesAnyId(member.user, id)
            || this.matchesAnyId(member.user_uuid, id),
        ),
    )
    if (adminMember) {
      return true
    }

    return matrixRoomAdmin
  }

  static isCurrentUserAdmin(
    chat: ChatOut,
    currentUserId: string | number | null | undefined,
    context: GroupAdminContext = {},
  ): boolean {
    if (!chat) {
      return false
    }

    if (!isGroupChat(chat)) {
      return false
    }

    if (!currentUserId && !context.matrixUserId) {
      return false
    }

    return this.isGroupAdmin(chat, currentUserId, context)
  }

  static canManageChatSettings(
    chat: ChatOut,
    currentUserId: string | number | null | undefined,
    context: GroupAdminContext = {},
  ): boolean {
    if (!chat) {
      return false
    }

    if (isEventChat(chat)) {
      return false
    }

    return isGroupLikeChatType(chat.type)
      && this.isCurrentUserAdmin(chat, currentUserId, context)
  }

  static isEventChat(chat: ChatOut | null | undefined): boolean {
    if (!chat) {
      return false
    }

    return chat.type === CHAT_TYPES.EVENT
  }

  static canEditChat(chat: ChatOut | null | undefined): boolean {
    if (!chat) {
      return false
    }

    return !this.isEventChat(chat)
  }

  static isGroupOrChannel(chat: ChatOut | null | undefined): boolean {
    if (!chat) {
      return false
    }

    return isGroupLikeChatType(chat.type)
  }

  /** Канал: только создатель и админы. Остальные типы — capabilities.can_post или true. */
  static canPostInChat(
    chat: Pick<ChatOut, 'type' | 'owner' | 'created_by'> & {
      capabilities?: { can_post?: boolean; can_manage_members?: boolean }
      members?: Array<{
        is_admin?: boolean
        user?: string | number
        user_uuid?: string
        user_id?: string | number
      }>
    } | null | undefined,
    currentUserId: string | number | null | undefined,
    matrixUserId?: string | null,
  ): boolean {
    if (!chat) {
      return false
    }

    if (chat.type === CHAT_TYPES.CHANNEL) {
      if (chat.capabilities?.can_post === false) {
        return false
      }

      // Тот же критерий, что и «Управление каналом» с бэка
      if (chat.capabilities?.can_manage_members === true) {
        return true
      }

      return this.isChannelPoster(chat, currentUserId, matrixUserId)
    }

    if (typeof chat.capabilities?.can_post === 'boolean') {
      return chat.capabilities.can_post
    }

    return true
  }

  private static isChannelPoster(
    chat: Pick<ChatOut, 'owner' | 'created_by'> & {
      members?: Array<{
        is_admin?: boolean
        user?: string | number
        user_uuid?: string
        user_id?: string | number
      }>
    },
    currentUserId: string | number | null | undefined,
    matrixUserId?: string | null,
  ): boolean {
    const userIds = [currentUserId, matrixUserId].filter(
      id => id != null && id !== '',
    )
    if (!userIds.length) {
      return false
    }

    if (chat.owner && userIds.some(id => this.matchesAnyId(chat.owner, id))) {
      return true
    }

    if (chat.created_by) {
      const creator = chat.created_by as { id?: string | number; uuid?: string | number }
      if (userIds.some(id => this.matchesAnyId(id, creator.id, creator.uuid))) {
        return true
      }
    }

    return (chat.members || []).some(
      member =>
        Boolean(member.is_admin)
        && userIds.some(id =>
          this.matchesAnyId(
            id,
            member.user,
            member.user_uuid,
            member.user_id,
          ),
        ),
    )
  }
}
